import { apiClient } from 'services/api';
import { documentServiceClient } from 'services/api/client';
import type { ProspectType } from 'services/repositories/interfaces/ProspectRepository';
import SnakeCaseKeys from 'snakecase-keys';
import { Endpoints } from 'utils/endpoints';
import type { CreateDocumentApiResponse, DocumentRepository, DocumentSectionsResponse, DocumentUpdateResponse } from './DocumentRepository';
import type { DocumentStatus, PricingTotalType, SigneeEntity } from './entities';
import type { DocumentsPublicControllerGetDocumentMetadataByDocumentIdQueryResponse } from '@proposify/document-service-api-schema';
import { parseDocumentMetadata } from '../repositories/implementations/ApiDocumentServiceRepository.ts';
import type { ProspectOption } from '../../components/editor/SidePanel/DocumentSettings/useProspect.ts';

export interface CreateDocumentApiPayload {
  title?: string;
  price?: number;
  content_id?: string;
}

interface UpdateDocumentApiPayload {
  title?: string;
  price?: number | null;
  assigned_user?: { id: number } | null;
  prospect?: {
    id: number | string;
    type: ProspectOption['type'];
    contact?: { id: string; name: string; email: string } | null;
  } | null;
}

export type GetDocumentByIdApiResponse = {
  id: string;
  title: string;
  price: number;
  status: DocumentStatus;
  company: { id: number; name: string };
  is_locked: boolean;
  assigned_user?: { id: number; name: string } | null;
  prospect?: { id: number; name: string; type: ProspectType; contact?: null | { id: number; name: string; email: string } } | null;
  document_belongs_to_same_account: boolean;
  content_id: string;
  account_id: number;
  total_type: PricingTotalType;
  created_by: string;
};

interface SigneeApiObject {
  id: number | string;
  first_name: string;
  last_name: string;
  email: string;
  signee_type: 'USER' | 'PERSON';
}

interface SigneesApiResponse {
  signees: Array<SigneeApiObject>;
}

function Signee({ id, first_name, last_name, email, signee_type }: SigneeApiObject): SigneeEntity {
  return {
    uid: `${id}_${signee_type}`,
    id,
    firstName: first_name,
    lastName: last_name,
    email,
    signeeType: signee_type,
    fullName: [first_name, last_name].filter(Boolean).join(' '),
  };
}

export const apiDocumentRepository: DocumentRepository = {
  createDocument: async (payload) => {
    const data: CreateDocumentApiPayload = {
      title: payload.title,
      price: payload.price,
      content_id: payload.contentId,
    };
    const { document_id } = await apiClient.post<CreateDocumentApiResponse>(Endpoints.documents, data);
    return {
      documentId: document_id,
    };
  },
  updateDocument: async ({ documentId, ...payload }) => {
    const data = SnakeCaseKeys(payload);
    const resp = await apiClient.patch<DocumentUpdateResponse, UpdateDocumentApiPayload>(Endpoints.documentDetail(documentId), data);
    const { status } = resp;

    return { status };
  },
  getDocumentById: async (documentId) => {
    const url = Endpoints.previewDetails(documentId);
    return parseDocumentMetadata(await apiClient.get<DocumentsPublicControllerGetDocumentMetadataByDocumentIdQueryResponse>(url));
  },
  getSignees: async (accountId, params) => {
    const response = await documentServiceClient.get<SigneesApiResponse>(Endpoints.accountSignees(accountId), { params: params });
    return {
      signees: response.signees.map((signee) => Signee(signee)),
    };
  },
  getSectionsByDocumentId: async (documentId) => {
    return await apiClient.get<DocumentSectionsResponse>(Endpoints.sectionsBlocksForPreview(documentId));
  },
};
